import { IAssertPayload } from '@shared/asserts/i_assert_payload';
import { assert_not_null } from '@shared/asserts/utils_asserts';

declare global {
  interface Map<K, V> {
    /**
     * Retrieves the value for the given key or throws an error if the key is not found.
     *
     * @param {K} key - The key to look up in the map.
     * @param {string} [reference] - An optional reference string for error context.
     * @param {Record<string, unknown>} [payload] - Additional context data for debugging.
     * @returns {V} The value associated with the key.
     * @throws {Error} If the key is not found in the map.
     */
    get_or_assert(key: K, payload?: IAssertPayload): V;

    /**
     * Retrieves the value for the given key or returns the default value if the key is not found.
     *
     * **Does not modify the map.**
     *
     * @param {K} key - The key to look up in the map.
     * @param {V} default_value - The default value to return if the key does not exist.
     * @returns {V} The value associated with the key, or the provided default value.
     */
    get_value_or_default(key: K, default_value: V): V;

    /**
     * Retrieves the value for the given key or creates a new **Map<K, V>** if the key is not found.
     *
     * **Modifies the map by adding a new Map if the key does not exist.**
     *
     * @param {K} key - The key to look up in the map.
     * @returns {V} The existing or newly created map.
     */
    get_or_create_map(key: K): V;

    /**
     * Retrieves the value for the given key or creates a new **Array<T>** if the key is not found.
     *
     * **Modifies the map by adding a new empty array if the key does not exist.**
     *
     * @param {K} key - The key to look up in the map.
     * @returns {V} The existing or newly created array.
     */
    get_or_create_array(key: K): V;

    /**
     * Retrieves the value for the given key or creates a new **Set<T>** if the key is not found.
     *
     * **Modifies the map by adding a new empty set if the key does not exist.**
     *
     * @param {K} key - The key to look up in the map.
     * @returns {V} The existing or newly created set.
     */
    get_or_create_set(key: K): V;

    /**
     * Adds an item to an array stored in the map at the given key.
     *
     * If the key does not exist, a new array is created and the item is added.
     *
     * **Modifies the map by adding a new array if the key does not exist.**
     *
     * @param {K} key - The key associated with the array.
     * @param {T} item - The item to add to the array.
     * @returns {void} Nothing is returned.
     */
    add_item_to_array<T>(key: K, item: T): V extends T[] ? void : never;

    is_empty(): boolean;

    values_list(): V[];
  }
}

Map.prototype.get_or_assert = function <K, V>(key: K, payload: IAssertPayload = {}): V {
  const result = this.get(key);

  assert_not_null(result, { ...payload, key });

  return result as V;
};

Map.prototype.get_value_or_default = function <K, V>(key: K, default_value: V): V {
  let out = this.get(key);

  if (!out) {
    out = default_value;
  }

  return out;
};

Map.prototype.get_or_create_map = function <K, V>(key: K): V {
  let out = this.get(key);

  if (!out) {
    out = new Map();
    this.set(key, out);
  }

  return out as V;
};

Map.prototype.get_or_create_array = function <K, V>(key: K): V {
  let out = this.get(key);

  if (!out) {
    out = [];
    this.set(key, out);
  }

  return out as V;
};

Map.prototype.get_or_create_set = function <K, V>(key: K): V {
  let out = this.get(key);

  if (!out) {
    out = new Set();
    this.set(key, out);
  }

  return out as V;
};

Map.prototype.add_item_to_array = function <K, T>(key: K, item: T): void {
  const array = this.get_or_create_array(key) as T[];

  array.push(item);
};

Map.prototype.is_empty = function () {
  return this.size == 0;
};

Map.prototype.values_list = function <K, V>(): V[] {
  return Array.from(this.values());
};

export {}; // This ensures TypeScript treats it as a module
